import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import moment from 'moment/moment';
import translate from '../../../../commonComponents/translate';
import Loader from '../../../../services/loader';
import Title from '../../../../commonComponents/Title';
import ContentContainer from '../../../../commonComponents/pageLayout/ContentContainer';
import SubHeader from '../../../../commonComponents/pageLayout/SubHeader';
import TopicBanner from '../../../../commonComponents/pageLayout/header/TopicBanner';
import CareerFilters from './CareerFilters';
import GlobalSummary from './GlobalSummary';
import MonthlySummary from './MonthlySummary';
import Notifications from '../../../../services/notifications';
import LoggedUserContext from '../../../../contextProviders/LoggedUserContext';
import LevelObjectives from './LevelObjectives';
import FastStartBonus from './FastStartBonus';

function MyCareer(props) {
  const { t, _csrf } = props;

  const { loggedUser } = useContext(LoggedUserContext);
  const { _id: loggedUserId } = loggedUser;

  const loader = useRef(new Loader('#neofin-my-career'));
  const [isLoading, setLoading] = useState(false);

  useEffect(() => {
    if (loader.current) {
      if (isLoading) {
        loader.current.show();
      } else {
        loader.current.hide();
      }
    }
  }, [isLoading]);

  const [globalSummaryData, setGlobalSummaryData] = useState({
    isActive: false,
    activeUntilDate: Date.now(),
    isBusinessLicenseActive: false,
    businessLicenseActiveUntilDate: Date.now(),
    percentageCommission: 0,
    weeklyCapping: 0,
    currencyInfo: {},
  });

  const [monthlySummaryData, setMonthlySummaryData] = useState({
    myVA: 0,
    VALeft: 0,
    VARight: 0,
    personalVA: 0,
    groupVA: 0,
  });

  const [levelObjectivesData, setLevelObjectivesData] = useState([]);

  const [fastStartBonus, setFastStartBonus] = useState([]);

  // List
  const startOfMonth = moment().utc().startOf('month').valueOf();
  const today = moment().utc().endOf('day').valueOf();

  const [filters, setFilters] = useState({
    user: loggedUserId,
    dateStart: null,
    dateEnd: null,
  });

  const [collaboratorSince, setCollaboratorSince] = useState(today);

  const handleFiltersChange = useCallback(
    field => value =>
      setFilters(prevState => ({ ...prevState, [field]: value })),
    [setFilters],
  );
  const handleDateChange = (dateStart, dateEnd) => {
    console.log('here', dateStart, dateEnd);
    setFilters(prevState => ({
      ...prevState,
      dateStart,
      dateEnd,
    }));
  };

  const handleGetGlobalSummary = useCallback(() => {
    axios
      .get(`/api/tailor-made/neofin/career/summary/global/${filters.user}`, {
        params: {},
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'Games',
          'X-CSRF-Token': _csrf,
          _csrf,
        },
      })
      .then(({ data: response }) => {
        setGlobalSummaryData(response.data);
      })
      .catch(err => {
        console.log(err);
        Notifications.showErrorFromRequest(err, 'error_fetchGlobalSummaryData');
      });
  }, [filters]);

  const handleGetMonthlySummary = useCallback(() => {
    axios
      .get(`/api/tailor-made/neofin/career/summary/month/${filters.user}`, {
        params:
          filters.dateStart && filters.dateEnd
            ? { dateStart: filters.dateStart, dateEnd: filters.dateEnd }
            : {},
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'Games',
          'X-CSRF-Token': _csrf,
          _csrf,
        },
      })
      .then(({ data: response }) => {
        setMonthlySummaryData(response.data);
      })
      .catch(err => {
        console.log(err);
        Notifications.showErrorFromRequest(
          err,
          'error_fetchMonthlySummaryData',
        );
      });
  }, [filters]);

  const handleGetLevelObjectives = useCallback(() => {
    axios
      .get(`/api/tailor-made/neofin/career/level/objectives/${filters.user}`, {
        params: {},
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'Games',
          'X-CSRF-Token': _csrf,
          _csrf,
        },
      })
      .then(({ data: response }) => {
        setLevelObjectivesData(response.data);
      })
      .catch(err => {
        console.log(err);
        Notifications.showErrorFromRequest(err, 'error_fetchGlobalSummaryData');
      });
  }, [filters.user]);

  const handleGetFastStartBonus = useCallback(() => {
    axios
      .get(`/api/tailor-made/neofin/career/fast-start/bonus/${filters.user}`, {
        params: {},
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'Games',
          'X-CSRF-Token': _csrf,
          _csrf,
        },
      })
      .then(({ data: response }) => {
        setFastStartBonus(response.data);
      })
      .catch(err => {
        console.log(err);
        Notifications.showErrorFromRequest(err, 'error_fetchGlobalSummaryData');
      });
  }, [filters.user]);

  const handleGetCollaboratorSince = useCallback(() => {
    axios
      .get(`/api/user/direct-selling/collaborator-date/${filters.user}`, {
        params: {},
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'Organization',
          'X-CSRF-Token': _csrf,
          _csrf,
        },
      })
      .then(({ data: response }) => {
        setCollaboratorSince(response.data?.collaboratorDate);
      })
      .catch(err => {
        console.log(err);
        Notifications.showErrorFromRequest(err, 'error_fetchGlobalSummaryData');
      });
  }, [filters.user]);

  useEffect(() => {
    setLoading(true);
    handleGetMonthlySummary();
    setLoading(false);
  }, [filters]);
  useEffect(() => {
    setLoading(true);
    handleGetGlobalSummary();
    handleGetLevelObjectives();
    handleGetFastStartBonus();
    handleGetCollaboratorSince();
    setLoading(false);
  }, [filters.user]);

  return (
    <Title title={t('header_myCareer')}>
      <ContentContainer>
        <SubHeader title={t('header_myCareer')} />
        <TopicBanner />
        <div className="m-content" id="neofin-my-career">
          <div className="full-page-container-with-subheader">
            <CareerFilters
              t={t}
              onFilterChange={handleFiltersChange}
              filters={filters}
            />
            <div className="career-summaries">
              <GlobalSummary t={t} data={globalSummaryData} />
              <MonthlySummary
                t={t}
                data={monthlySummaryData}
                filters={filters}
                onDateChange={handleDateChange}
              />
            </div>
            <LevelObjectives t={t} data={levelObjectivesData} />
            <FastStartBonus
              t={t}
              data={fastStartBonus}
              dateData={collaboratorSince}
            />
          </div>
        </div>
      </ContentContainer>
    </Title>
  );
}

MyCareer.propTypes = {
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
};

export default translate(MyCareer);
